"use client";

import { Box, Card, Table, Text, Title } from "@mantine/core";
import clsx from "clsx";
import { ReactNode } from "react";
import classes from "./SubjectCardTable.module.css";

export type SubjectCardTableHeader = {
  title: ReactNode;
  badge?: ReactNode;
  description?: string;
};

export type SubjectCardTableRow = {
  first: ReactNode;
  second: ReactNode;
  third?: ReactNode;
};

export type SubjectCardTableProps = {
  id?: string;
  className?: string;
  header?: SubjectCardTableHeader;
  columns: { first: string; second: string; third?: string };
  rows: SubjectCardTableRow[];
  firstColumnWidth?: string;
};

const DEFAULT_FIRST_COLUMN_WIDTH = "100px";

export default function SubjectCardTable({
  id,
  className,
  header,
  columns,
  rows,
  firstColumnWidth = DEFAULT_FIRST_COLUMN_WIDTH,
}: SubjectCardTableProps) {
  const hasThird = Boolean(columns.third);
  const cssVars = {
    "--sct-first-col-width": firstColumnWidth ?? DEFAULT_FIRST_COLUMN_WIDTH,
    "--sct-second-col-width": hasThird ? "44%" : "88%",
  } as React.CSSProperties;

  return (
    <Card id={id} className={clsx(classes.card, className)} style={cssVars}>
      {header && (
        <Box className={classes.headerBox}>
          <Box className={classes.headerGroup}>
            <Title order={2} className={classes.headerTitle}>
              {header.title}
            </Title>
            {header.badge != null && (
              <Text className={classes.headerBadge}>{header.badge}</Text>
            )}
          </Box>
          {header.description != null && header.description !== "" && (
            <Text
              component="div"
              className={classes.headerDescription}
              dangerouslySetInnerHTML={{ __html: header.description }}
            />
          )}
        </Box>
      )}

      <Box className={classes.tableWrapper}>
        <Table
          verticalSpacing="md"
          fz="sm"
          striped
          withColumnBorders={false}
          className={classes.table}
        >
          <colgroup>
            <col className={classes.colFirst} />
            <col className={classes.colSecond} />
            {hasThird && <col className={classes.colThird} />}
          </colgroup>
          <thead className={classes.thead}>
            <tr>
              <th className={classes.thFirst}>
                <span className={classes.thFirstInner}>{columns.first}</span>
              </th>
              <th className={classes.thRest}>{columns.second}</th>
              {hasThird && <th className={classes.thRest}>{columns.third}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td className={classes.tdFirst}>
                  <span className={classes.tdFirstInner}>{row.first}</span>
                </td>
                <td className={classes.tdRest}>{row.second}</td>
                {hasThird && (
                  <td className={classes.tdRest}>{row.third ?? null}</td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </Box>
    </Card>
  );
}
